import { useEffect, useRef, useState } from "react";
import { PRODUCT_CARD_OPTIONS } from "../../../constants/common/splideOptions";
import { HOVER_IMAGE_LOAD_DELAY_MS, subscribeToScrollHalt } from "./scrollHalt";

const SLIDE_INTERVAL_MS = PRODUCT_CARD_OPTIONS.autoplaySpeed || 1000;

/**
 * Last index of a request pair. The first photo stays alone; later photos are 1-2, then 3-4, then 5-6.
 * @param {number} index
 * @returns {number}
 */
const endOfImagePair = (index) => {
  if (index <= 0) return 0;
  return index % 2 === 0 ? index : index + 1;
};

/**
 * Loads later card images two at a time after a still hover.
 * The first image change runs as soon as the next image is ready.
 * A playing video holds that change until the video ends.
 * @param {Object} params
 * @param {React.RefObject} params.sliderRef
 * @param {React.MutableRefObject<string[]>} params.productImgListRef
 */
export function useCardHoverImages({ sliderRef, productImgListRef }) {
  const hoverIntentRef = useRef(null);
  const hoverLoadTimerRef = useRef(null);
  const advanceTimerRef = useRef(null);
  const wantsAutoplayRef = useRef(false);
  const waitingForImageRef = useRef(false);
  const firstMovePendingRef = useRef(true);
  const holdForVideoRef = useRef(false);
  const currentSlideRef = useRef(0);
  const loadedSlideIndexesRef = useRef(new Set([0]));
  const [mountedThroughIndex, setMountedThroughIndex] = useState(0);
  const [readySlideCount, setReadySlideCount] = useState(1);
  const mountedThroughIndexRef = useRef(0);
  const advanceWhenReadyRef = useRef(() => {});

  mountedThroughIndexRef.current = mountedThroughIndex;

  /**
   * @returns {number}
   */
  const getNextSlide = () => {
    const count = productImgListRef.current.length;
    const current = currentSlideRef.current;
    if (count <= 1) return current;
    return current + 1 >= count ? 0 : current + 1;
  };

  /**
   * Requests images through this index. Earlier images stay mounted.
   * @param {number} index
   */
  const revealThrough = (index) => {
    const lastIndex = Math.max(productImgListRef.current.length - 1, 0);
    const target = Math.min(lastIndex, Math.max(mountedThroughIndexRef.current, index));
    if (target === mountedThroughIndexRef.current) return;
    mountedThroughIndexRef.current = target;
    setMountedThroughIndex(target);
  };

  advanceWhenReadyRef.current = () => {
    window.clearTimeout(advanceTimerRef.current);
    advanceTimerRef.current = null;
    if (holdForVideoRef.current || !wantsAutoplayRef.current) return;
    if (!loadedSlideIndexesRef.current.has(getNextSlide())) {
      waitingForImageRef.current = true;
      return;
    }

    waitingForImageRef.current = false;
    const delay = firstMovePendingRef.current ? 0 : SLIDE_INTERVAL_MS;
    advanceTimerRef.current = window.setTimeout(() => {
      advanceTimerRef.current = null;
      if (holdForVideoRef.current || !wantsAutoplayRef.current) return;
      if (!loadedSlideIndexesRef.current.has(getNextSlide())) {
        waitingForImageRef.current = true;
        return;
      }
      waitingForImageRef.current = false;
      firstMovePendingRef.current = false;
      const destination = getNextSlide();
      currentSlideRef.current = destination;
      sliderRef.current?.slickNext();
      revealThrough(endOfImagePair(destination + 1));
      advanceWhenReadyRef.current();
    }, delay);
  };

  /**
   * Requests the next pair, then slides once the immediate next image is ready.
   */
  const mountRemainingImages = () => {
    if (productImgListRef.current.length <= 1) return;
    revealThrough(endOfImagePair(currentSlideRef.current + 1));
    if (!wantsAutoplayRef.current) return;
    advanceWhenReadyRef.current();
  };

  useEffect(
    () =>
      subscribeToScrollHalt((hasHalted) => {
        window.clearTimeout(hoverLoadTimerRef.current);
        hoverLoadTimerRef.current = null;
        const sliderIsHovered = sliderRef.current?.innerSlider?.list?.matches?.(":hover");
        if (hasHalted && hoverIntentRef.current && sliderIsHovered) mountRemainingImages();
      }),
    [],
  );

  /**
   * Marks a slide ready. A loaded next image moves immediately on the first change.
   * @param {number} index
   */
  const onSlideImageReady = (index) => {
    const alreadyReady = loadedSlideIndexesRef.current.has(index);
    if (!alreadyReady) {
      loadedSlideIndexesRef.current.add(index);
      setReadySlideCount(loadedSlideIndexesRef.current.size);
    }
    if (holdForVideoRef.current) return;
    if (!wantsAutoplayRef.current || (alreadyReady && !waitingForImageRef.current)) return;
    advanceWhenReadyRef.current();
  };

  /** Keeps the slider on the video until that video ends. */
  const holdForVideo = () => {
    holdForVideoRef.current = true;
    wantsAutoplayRef.current = false;
    waitingForImageRef.current = false;
    window.clearTimeout(advanceTimerRef.current);
    advanceTimerRef.current = null;
  };

  /**
   * Shows the next image after the video ends. A loaded image is shown immediately.
   */
  const continueAfterVideo = () => {
    holdForVideoRef.current = false;
    if (!hoverIntentRef.current) return;
    wantsAutoplayRef.current = true;
    hoverIntentRef.current = "autoplay";
    revealThrough(endOfImagePair(currentSlideRef.current + 1));
    advanceWhenReadyRef.current();
  };

  /**
   * @param {boolean} shouldStartAutoplay
   * @param {boolean} [shouldLoadNow] Request the next pair immediately instead of waiting out the hover delay.
   * @returns {boolean} Whether the image request was deferred.
   */
  const requestHoverImages = (shouldStartAutoplay, shouldLoadNow = false) => {
    hoverIntentRef.current = shouldStartAutoplay ? "autoplay" : "images";
    if (shouldStartAutoplay) wantsAutoplayRef.current = true;
    const imagesAlreadyRequested = mountedThroughIndexRef.current > 0;
    if (shouldLoadNow || imagesAlreadyRequested || productImgListRef.current.length <= 1) {
      if (shouldLoadNow || shouldStartAutoplay) mountRemainingImages();
      return false;
    }
    window.clearTimeout(hoverLoadTimerRef.current);
    hoverLoadTimerRef.current = window.setTimeout(() => {
      hoverLoadTimerRef.current = null;
      mountRemainingImages();
    }, HOVER_IMAGE_LOAD_DELAY_MS);
    return true;
  };

  /** Clears a pending hover load and stops further slide advances. */
  const cancelHoverImages = () => {
    hoverIntentRef.current = null;
    wantsAutoplayRef.current = false;
    holdForVideoRef.current = false;
    waitingForImageRef.current = false;
    firstMovePendingRef.current = true;
    currentSlideRef.current = 0;
    window.clearTimeout(hoverLoadTimerRef.current);
    window.clearTimeout(advanceTimerRef.current);
    hoverLoadTimerRef.current = null;
    advanceTimerRef.current = null;
  };

  useEffect(
    () => () => {
      window.clearTimeout(hoverLoadTimerRef.current);
      window.clearTimeout(advanceTimerRef.current);
    },
    [],
  );

  return {
    mountedThroughIndex,
    areAllSlidesReady: readySlideCount >= productImgListRef.current.length,
    onSlideImageReady,
    requestHoverImages,
    holdForVideo,
    continueAfterVideo,
    cancelHoverImages,
    holdForVideoRef,
  };
}
